import { ProfileBio } from '@atoms/ProfileBio';
import ProfileImg from '@atoms/ProfileImg';
import useBlockedUsers from '@contexts/useBlockedUsers';
import { Menu01Icon, PencilEdit01Icon } from '@hugeicons/core-free-icons';
import FollowButton from '@molecules/FollowButton';
import HeaderButton from '@molecules/HeaderButton';
import ProfileSiteButtons from '@molecules/ProfileSiteButtons';
import ProfileStats from '@molecules/ProfileStats';
import {
  ProfileViewMode,
  ProfileViewToggle,
} from '@molecules/ProfileViewToggle';
import UserSettingsModal from '@molecules/UserSettingsModal';
import UserTicketsList from '@organisms/UserTicketsList';
import { useGetFollowing, useUnfollowUser } from '@queries/follows';
import { useBlockUser, useGetProfile } from '@sunrise-ui/api-client';
import { TemporalRelation } from '@sunrise-ui/api/events/api';
import { type Profile } from '@sunrise-ui/api/profile';
import {
  IconButton,
  Mobile,
  NonMobile,
  ProfileBasicInfo,
  TextReg,
  menuContentStyle,
  useAuthStore,
  useDynamicStyles,
  useMediaQueries,
  useSetHeader,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { defaultBack } from '@utils/navigation';
import { headerOptionsCreator } from '@utils/screenOptions';
import { router } from 'expo-router';
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Menu } from 'react-native-paper';

export default function ProfileView({ userId }: { userId?: string }) {
  const { isMobile, isTablet, isDesktop } = useMediaQueries();
  const { isUserBlocked } = useBlockedUsers();
  const { currentUserId } = useAuthStore();
  const isOwnProfile = userId === currentUserId;
  const { isLoading: isLoadingProfile, data: profile } = useGetProfile(userId);
  const styles = useDynamicStyles(createStyles, { profile });
  const [menuVisible, setMenuVisible] = useState(false);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [profileViewMode, setProfileViewMode] = useState<ProfileViewMode>(
    ProfileViewMode.Upcoming,
  );

  const temporalRelation =
    profileViewMode === ProfileViewMode.Past
      ? TemporalRelation.Past
      : TemporalRelation.Future;

  const { mutateAsync: blockUser } = useBlockUser(userId);
  const { data: following, isLoading: isLoadingFollowing } = useGetFollowing();
  const { mutateAsync: unfollowUser } = useUnfollowUser();

  const isFollowingUser = Boolean(
    following?.find((entity) => entity.entity_id === userId),
  );

  const navigateToEditProfile = () => {
    router.navigate('/editProfile');
  };

  const ownProfileHeaderLeft = <HeaderButton onPress={defaultBack()} />;

  const ownProfileHeaderRight = (
    <View style={styles.headerRight}>
      {!isDesktop && (
        <HeaderButton icon={PencilEdit01Icon} onPress={navigateToEditProfile} />
      )}
      <Menu
        visible={menuVisible}
        contentStyle={menuContentStyle}
        onDismiss={() => setMenuVisible(false)}
        anchor={
          <HeaderButton
            icon={Menu01Icon}
            onPress={() => setMenuVisible(true)}
          />
        }
        anchorPosition="bottom"
      >
        <Menu.Item
          onPress={() => {
            setMenuVisible(false);
            setSettingsModalVisible(true);
          }}
          title="Settings"
        />
      </Menu>
    </View>
  );

  const otherProfileHeaderLeft = <HeaderButton onPress={defaultBack()} />;

  const otherProfileHeaderRight = (
    <Menu
      visible={menuVisible}
      contentStyle={menuContentStyle}
      onDismiss={() => setMenuVisible(false)}
      anchor={
        <HeaderButton icon={Menu01Icon} onPress={() => setMenuVisible(true)} />
      }
      anchorPosition="bottom"
    >
      <Menu.Item
        onPress={async () => {
          setMenuVisible(false);
          try {
            if (isFollowingUser && userId) await unfollowUser(userId);
            await blockUser();
            router.navigate('/');
          } catch {
            return;
          }
        }}
        disabled={!userId || isLoadingFollowing}
        title="Block"
      />
    </Menu>
  );

  useSetHeader(
    headerOptionsCreator(
      isOwnProfile ? ownProfileHeaderLeft : otherProfileHeaderLeft,
      isOwnProfile ? ownProfileHeaderRight : otherProfileHeaderRight,
    ),
  );

  const nameButton = isOwnProfile ? (
    isDesktop && (
      <IconButton
        icon={PencilEdit01Icon}
        onPress={navigateToEditProfile}
        size="medium"
      />
    )
  ) : (
    <FollowButton userId={userId} />
  );

  if (userId && ((!isLoadingProfile && !profile) || isUserBlocked(userId))) {
    return (
      <View style={styles.container}>
        <TextReg>No user here. Creepy.</TextReg>
      </View>
    );
  }

  const HeaderComponent = (
    <View style={styles.container}>
      <View style={styles.topSection}>
        <ProfileImg
          userId={userId}
          imgSize={isMobile ? 'small' : isTablet ? 'medium' : 'large'}
          styleSize={isMobile ? 75 : isTablet ? 225 : 300}
        />
        <View style={styles.profileText}>
          <ProfileBasicInfo
            firstName={profile?.first_name}
            lastName={profile?.last_name}
            pronouns={profile?.pronouns}
            headline={profile?.headline}
            isLoading={isLoadingProfile}
            nameButton={nameButton}
            style={styles.basicInfo}
          />
          <NonMobile>
            <ProfileStats userId={userId} />
            {(profile?.links?.linkedin ??
              profile?.links?.github ??
              profile?.links?.x ??
              profile?.links?.website) && (
              <ProfileSiteButtons userId={userId} />
            )}
          </NonMobile>
        </View>
      </View>
      <Mobile>
        <ProfileStats userId={userId} />
        {(profile?.links?.linkedin ??
          profile?.links?.github ??
          profile?.links?.x ??
          profile?.links?.website) && <ProfileSiteButtons userId={userId} />}
      </Mobile>

      {profile?.bio && (
        <View style={styles.bioContainer}>
          <ProfileBio userId={userId} />
        </View>
      )}

      <View style={styles.controlsFullRow}>
        <View style={styles.leftControls}>
          <ProfileViewToggle
            value={profileViewMode}
            onValueChange={setProfileViewMode}
          />
        </View>
      </View>
    </View>
  );

  return (
    <>
      <UserTicketsList
        userId={userId}
        ListHeaderComponent={HeaderComponent}
        temporalRelation={temporalRelation}
      />
      <UserSettingsModal
        visible={settingsModalVisible}
        hideModal={() => setSettingsModalVisible(false)}
      />
    </>
  );
}

const createStyles = ({
  isMobile,
  isTablet,
  profile,
}: WithResponsive<{ profile?: Profile }>) =>
  StyleSheet.create({
    container: {
      alignItems: 'center',
      gap: 20,
      padding: isMobile ? 12 : isTablet ? 16 : 20,
    },
    topSection: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 20,
    },
    profileText: {
      alignItems: 'flex-start',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      flexShrink: 1,
    },
    basicInfo: {
      maxWidth: '100%',
      width: !profile && isMobile ? 200 : undefined,
    },
    bioContainer: {
      width: '100%',
      paddingHorizontal: isMobile ? 8 : isTablet ? 12 : 16,
      maxWidth: isMobile ? 400 : 820,
    },
    controlsFullRow: {
      width: '100%',
      maxWidth: isMobile ? 400 : 820,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 16,
      paddingHorizontal: isMobile ? 8 : 0,
    },
    leftControls: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    headerRight: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
  });
